import { Injectable } from "@angular/core";

import {
    IService,
    ITCIAppCustomizationParam,
    ITCIAppCustomizationProvider,
    ITCIAppCustomizationRequest,
    ITCIAppCustomizationResponse,
    ITCIEnvVars,
    TCI_APP_CUSTOMIZATION_TYPE,
    TCI_ENVIRONMENT_VAR_TYPE
} from "@tci/common";
import * as Ajv from "ajv";
import * as lodash from "lodash";
import { Observable } from "rxjs";
import { BaseTCIAppCustomizationProvider } from "../../ng-tenant/tci-tenant-services";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";
import { TciTenantService } from "../../ng-tenant/tci-tenant.service";
import schema from "../ng-schemas/ng-customization.schema";
import { NgEnvVarsService } from "./ng-env-vars.service";
import { NgUiDiscovery } from "./ng-ui-discovery";

export class NgTenantCustomizationProviderService {
    private static instance: NgTenantCustomizationProviderService;

    public static getInstance(
        envVarSvc: NgEnvVarsService,
        ngUiDiscovery: NgUiDiscovery
    ): NgTenantCustomizationProviderService {
        if (!NgTenantCustomizationProviderService.instance) {
            NgTenantCustomizationProviderService.instance = new NgTenantCustomizationProviderService(
                ngUiDiscovery,
                envVarSvc
            );
        }
        return NgTenantCustomizationProviderService.instance;
    }

    constructor(
        private ngUiDiscovery: NgUiDiscovery,
        private envVarSvc: NgEnvVarsService
    ) { }

    public getTenantCustomizationProviderService(): Observable<ITCIAppCustomizationProvider> {
        return this
            .ngUiDiscovery
            .getRemoteServicesWithModules()
            .mergeMap((services: IService[]) => services)
            .filter((service: IService) => !!(service.moduleConfig && service.moduleConfig.customization))
            .filter((service: IService) => {
                if (!NgCustomizationProviderService.validateJson(service.moduleConfig.customization)) {
                    const errorMsgPrompt = `The customization of tenant "${service.name}" didn't pass the JSON schema validation.`;

                    lodash.forEach(
                        NgCustomizationProviderService
                            .validateJson
                            .errors,
                        (error: Ajv.ErrorObject) => {
                            console.error(NgCustomizationProviderService.constructValidationErrorMessage(error));
                        }
                    );

                    throw errorMsgPrompt;
                } else {
                    return true;
                }
            })
            .take(1)
            .map((service: IService) => service.moduleConfig.customization)
            .map((customizationParams: { [key: string]: ITCIAppCustomizationParam }) => {
                return {
                    /**
                     * get customizations
                     * @param input
                     */
                    getCustomizations(input: ITCIAppCustomizationRequest): Observable<ITCIAppCustomizationResponse> {
                        const customizationParam: ITCIAppCustomizationParam = customizationParams[input.type];
                        let customization: ITCIAppCustomizationResponse;

                        if (customizationParam) {
                            customization = {
                                success: true,
                                msg: "",
                                result: customizationParam
                            };
                        } else {
                            customization = {
                                success: false,
                                msg: `Tenant customization type : ${input.type} not found`
                            };
                        }

                        return Observable.of(customization);
                    }
                } as ITCIAppCustomizationProvider;
            })
            .share();
    }
}

@Injectable()
export class NgCustomizationProviderService extends BaseTCIAppCustomizationProvider {

    static validateJson = new Ajv().compile(schema);
    private static instance: NgCustomizationProviderService;

    private tenantCustomizationProvider: ITCIAppCustomizationProvider;
    private tenantCustomizationProvider$: Observable<ITCIAppCustomizationProvider>;

    public static constructValidationErrorMessage(error: Ajv.ErrorObject): string {
        return `${error.dataPath} ${error.message}`;
    }

    public static getInstance(
        tenantSvc: TciTenantService,
        modelSvc: TciTenantModelService,
        envVarSvc: NgEnvVarsService,
        tenantCustomizationProviderServiceFactory: NgTenantCustomizationProviderService
    ) {
        if (!NgCustomizationProviderService.instance) {
            NgCustomizationProviderService.instance = new NgCustomizationProviderService(
                tenantSvc,
                modelSvc,
                envVarSvc,
                tenantCustomizationProviderServiceFactory
            );
        }

        return NgCustomizationProviderService.instance;
    }

    private static getDefaultCustomization(input: ITCIAppCustomizationRequest): ITCIAppCustomizationResponse {
        let result: ITCIAppCustomizationParam;

        switch (input.type) {
            case TCI_APP_CUSTOMIZATION_TYPE.GLOBAL_NAVBAR:
            case TCI_APP_CUSTOMIZATION_TYPE.HELP_PANEL:
            case TCI_APP_CUSTOMIZATION_TYPE.PENDO:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_MAIN_MENU_ENV_TOOLS:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_HEADER_MONITORING:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTERS:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTERS_APP_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTERS_STATUS_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTERS_APP_OWNER:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTERS_DEPLOY_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_FILTER_TAGS:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_DEPLOYMENT:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_INSTANCES:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_LAST_RUN:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_STATUS:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_ROW_HAMBURGER:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_ROW_PEEK_VIEW:
            // case TCI_APP_CUSTOMIZATION_TYPE.APP_DETAIL_DEPLOY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_DETAIL_HEADER_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_DETAIL_HEADER_CREATED_BY:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_DETAIL_HEADER_TAGS:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_CREATE:
            case TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_FILTER:
            case TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_COL_CATEGORY:
            case TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_COL_VISIBILITY:
            case TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_ROW_HAMBURGER:
            case TCI_APP_CUSTOMIZATION_TYPE.MARKETPLACE_LIST_FILTER:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_PEEK_VIEW:
            case TCI_APP_CUSTOMIZATION_TYPE.TCI_SCRIPTS_GOOGLE:
            case TCI_APP_CUSTOMIZATION_TYPE.TCI_SCRIPTS_PENDO:
                result = {
                    enabled: true,
                    visible: true,
                    tenantCustomization: false,
                    imageUrl: null,
                    label: null,
                    styleCss: null
                };
                break;
            case TCI_APP_CUSTOMIZATION_TYPE.DOCUMENT_TITLE:
                result = {
                    enabled: true,
                    visible: true,
                    tenantCustomization: false,
                    imageUrl: null,
                    label: "TIBCO Cloud Integration",
                    styleCss: null
                };
                break;
            case TCI_APP_CUSTOMIZATION_TYPE.APP_LIST_COL_WIDTH:
            case TCI_APP_CUSTOMIZATION_TYPE.APP_MAIN_MENU_EXTENSIONS:
                result = {
                    enabled: true,
                    visible: false,
                    tenantCustomization: false,
                    imageUrl: null,
                    label: null,
                    styleCss: null
                };
                break;
            default:
                return {
                    success: false,
                    result: {
                        enabled: false
                    }
                };
        }

        return {
            success: true,
            result
        };
    }

    public getCustomizations(input: ITCIAppCustomizationRequest): Observable<ITCIAppCustomizationResponse> {
        return this
            .tenantCustomizationProvider$
            .mergeMap(
                customizationProviderService => customizationProviderService ?
                    customizationProviderService.getCustomizations(input) :
                    Observable.of(NgCustomizationProviderService.getDefaultCustomization(input))
            )
            .map(customizations => customizations || NgCustomizationProviderService.getDefaultCustomization(input))
            .share();
    }

    private constructor(
        private tenantSvc: TciTenantService,
        private modelSvc: TciTenantModelService,
        private envVarSvc: NgEnvVarsService,
        private tenantCustomizationProviderServiceFactory: NgTenantCustomizationProviderService
    ) {
        super();
        this.tenantCustomizationProvider$ = envVarSvc
            .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.TCI_DOCKER_DEPLOYMENT)
            .mergeMap(
                (envVar: ITCIEnvVars<any>) => {
                    const value = envVar[TCI_ENVIRONMENT_VAR_TYPE.TCI_DOCKER_DEPLOYMENT];

                    if (lodash.isBoolean(value) && value) {
                        return this.retrieveTenantsJSONCustomizationProvider();
                    } else {
                        return Observable.of(null);
                    }
                }
            );
    }

    private retrieveTenantsJSONCustomizationProvider(): Observable<ITCIAppCustomizationProvider> {
        if (this.tenantCustomizationProvider) {
            return Observable.of(this.tenantCustomizationProvider);
        } else {
            return this
                .tenantCustomizationProviderServiceFactory
                .getTenantCustomizationProviderService();
        }
    }
}
